import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { DOSE_RULE_KINDS, type DoseRuleKind } from '@wodi/domain';
import type { DoseRuleView, ReplaceDoseRulesRequest } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  approveDoseRule,
  doseRulesQuery,
  formularyValueSetQuery,
  replaceDoseRules,
  retireDoseRule,
} from '../api/prescribing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import styles from '../prescribing/prescribing.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Dose limits (docs/plans/phase1-consultation.md §6, §7.5). Wodi ships no dose
// limit: each rule is entered by the clinical lead with its source (guideline, edition,
// page), used as "Provisional" until a second person approves it, and kept in history.
// Until then prescribing says "No dose limit set" for every drug.

export const KIND_LABELS: Record<DoseRuleKind, string> = {
  max_single_dose: 'Maximum per dose',
  max_daily_dose: 'Maximum per day',
  per_kg_single: 'Per kg per dose',
  per_kg_daily: 'Per kg per day',
  weight_band_dose: 'Dose for a weight band',
};

const DAYS_PER_YEAR = 365;

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function ageText(from: number, to: number | null): string {
  if (from === 0 && to === null) return 'All ages';
  const words = (days: number) =>
    days % DAYS_PER_YEAR === 0 ? `${String(days / DAYS_PER_YEAR)} y` : `${String(days)} days`;
  return to === null ? `From ${words(from)}` : `${words(from)} to under ${words(to)}`;
}

function amountText(rule: DoseRuleView): string {
  const per = rule.kind === 'per_kg_single' || rule.kind === 'per_kg_daily' ? '/kg' : '';
  if (rule.low !== null && rule.high !== null)
    return `${rule.low} to ${rule.high} ${rule.amountUnit}${per}`;
  if (rule.high !== null) return `Up to ${rule.high} ${rule.amountUnit}${per}`;
  return `From ${rule.low ?? ''} ${rule.amountUnit}${per}`;
}

function toDays(value: string, unit: 'years' | 'days'): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return null;
  return unit === 'years' ? n * DAYS_PER_YEAR : n;
}

interface NewRule {
  subject: string;
  kind: DoseRuleKind;
  routeCode: string;
  ageFrom: string;
  ageTo: string;
  ageUnit: 'years' | 'days';
  weightFrom: string;
  weightTo: string;
  low: string;
  high: string;
  amountUnit: string;
  enforcement: 'interruptive' | 'blocking';
  source: string;
}

const EMPTY: NewRule = {
  subject: '',
  kind: 'max_single_dose',
  routeCode: '',
  ageFrom: '0',
  ageTo: '',
  ageUnit: 'years',
  weightFrom: '',
  weightTo: '',
  low: '',
  high: '',
  amountUnit: 'mg',
  enforcement: 'interruptive',
  source: '',
};

function asInput(rule: DoseRuleView) {
  return {
    id: rule.id,
    routeCode: rule.routeCode,
    kind: rule.kind,
    ageFromDays: rule.ageFromDays,
    ageToDays: rule.ageToDays,
    weightFromKg: rule.weightFromKg,
    weightToKg: rule.weightToKg,
    low: rule.low,
    high: rule.high,
    amountUnit: rule.amountUnit,
    enforcement: rule.enforcement,
    source: rule.source,
  };
}

function AddRule({ rules }: { rules: readonly DoseRuleView[] }) {
  const queryClient = useQueryClient();
  const substances = useQuery(formularyValueSetQuery('drug_substance'));
  const routes = useQuery(formularyValueSetQuery('medication_route'));
  const [r, setR] = useState<NewRule>(EMPTY);
  const set = (patch: Partial<NewRule>) => {
    setR((current) => ({ ...current, ...patch }));
  };
  const weighted = r.kind === 'weight_band_dose';
  const ranged = weighted || r.kind === 'per_kg_single' || r.kind === 'per_kg_daily';
  const save = useMutation({
    mutationFn: () => {
      const existing = rules.filter(
        (x) => x.subjectType === 'substance' && x.subject === r.subject,
      );
      const body: ReplaceDoseRulesRequest = {
        subjectType: 'substance',
        subject: r.subject,
        rules: [
          ...existing.map(asInput),
          {
            routeCode: r.routeCode === '' ? null : r.routeCode,
            kind: r.kind,
            ageFromDays: toDays(r.ageFrom, r.ageUnit) ?? 0,
            ageToDays: toDays(r.ageTo, r.ageUnit),
            weightFromKg: weighted && r.weightFrom.trim() !== '' ? r.weightFrom.trim() : null,
            weightToKg: weighted && r.weightTo.trim() !== '' ? r.weightTo.trim() : null,
            low: ranged && r.low.trim() !== '' ? r.low.trim() : null,
            high: r.high.trim() === '' ? null : r.high.trim(),
            amountUnit: r.amountUnit.trim(),
            enforcement: r.enforcement,
            source: r.source.trim(),
          },
        ],
      };
      return replaceDoseRules(body);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(doseRulesQuery.queryKey, data);
      toasts.show({ title: 'Dose limit added as provisional' });
      setR({ ...EMPTY, subject: r.subject });
    },
  });
  return (
    <section aria-label="Add a dose limit" className={styles.rx}>
      <h2>Add a dose limit</h2>
      <div className={styles.fields}>
        <Select
          label="Substance"
          value={r.subject === '' ? null : r.subject}
          onChange={(k) => {
            set({ subject: String(k) });
          }}
          options={(substances.data ?? [])
            .filter((c) => c.status === 'active')
            .map((c) => ({ id: c.code, label: c.display }))}
          description="Covers every product containing it."
        />
        <Select
          label="Kind"
          value={r.kind}
          onChange={(k) => {
            set({ kind: String(k) as DoseRuleKind });
          }}
          options={DOSE_RULE_KINDS.map((k) => ({ id: k, label: KIND_LABELS[k] }))}
        />
        <Select
          label="Route"
          value={r.routeCode === '' ? 'any' : r.routeCode}
          onChange={(k) => {
            set({ routeCode: k === 'any' ? '' : String(k) });
          }}
          options={[
            { id: 'any', label: 'Any route' },
            ...(routes.data ?? []).map((c) => ({ id: c.code, label: c.display })),
          ]}
        />
      </div>
      <div className={styles.fields}>
        <TextField
          label="Age from"
          inputMode="numeric"
          value={r.ageFrom}
          onChange={(ageFrom) => {
            set({ ageFrom });
          }}
        />
        <TextField
          label="Age to (not including)"
          description="Empty: no upper age."
          inputMode="numeric"
          value={r.ageTo}
          onChange={(ageTo) => {
            set({ ageTo });
          }}
        />
        <RadioGroup
          label="Ages in"
          orientation="horizontal"
          value={r.ageUnit}
          onChange={(v) => {
            set({ ageUnit: v === 'days' ? 'days' : 'years' });
          }}
          options={[
            { id: 'years', label: 'Years' },
            { id: 'days', label: 'Days' },
          ]}
        />
        {weighted ? (
          <>
            <TextField
              label="Weight from"
              unit="kg"
              inputMode="decimal"
              value={r.weightFrom}
              onChange={(weightFrom) => {
                set({ weightFrom });
              }}
            />
            <TextField
              label="Weight to (not including)"
              unit="kg"
              inputMode="decimal"
              value={r.weightTo}
              onChange={(weightTo) => {
                set({ weightTo });
              }}
            />
          </>
        ) : null}
      </div>
      <div className={styles.fields}>
        {ranged ? (
          <TextField
            label={weighted ? 'Dose from' : 'Usual from'}
            inputMode="decimal"
            value={r.low}
            onChange={(low) => {
              set({ low });
            }}
          />
        ) : null}
        <TextField
          label={weighted ? 'Dose to' : 'Maximum'}
          inputMode="decimal"
          value={r.high}
          onChange={(high) => {
            set({ high });
          }}
        />
        <TextField
          label="Unit"
          description="mg, ug, g, mL, [iU], mmol"
          value={r.amountUnit}
          onChange={(amountUnit) => {
            set({ amountUnit });
          }}
        />
        <RadioGroup
          label="When exceeded"
          orientation="horizontal"
          value={r.enforcement}
          onChange={(v) => {
            set({ enforcement: v === 'blocking' ? 'blocking' : 'interruptive' });
          }}
          options={[
            { id: 'interruptive', label: 'Ask for a reason' },
            { id: 'blocking', label: 'Do not allow' },
          ]}
        />
      </div>
      <TextField
        label="Source"
        description="Guideline, edition and page. Required."
        value={r.source}
        onChange={(source) => {
          set({ source });
        }}
      />
      {save.isError ? (
        <Notice tone="error">
          {message(save.error, 'The limit was not added. Check it and try again.')}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={r.subject === '' || r.source.trim().length < 3}
          isPending={save.isPending}
          pendingLabel="Adding…"
          onPress={() => {
            save.mutate();
          }}
        >
          Add dose limit
        </Button>
      </div>
    </section>
  );
}

export function DoseRulesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const mayManage = session !== null && can(session, 'clinical_rules.manage');
  const mayApprove = session !== null && can(session, 'clinical_rules.approve');
  const rules = useQuery({ ...doseRulesQuery, enabled: mayManage || mayApprove });
  const [retiring, setRetiring] = useState<DoseRuleView | null>(null);
  const approve = useMutation({
    mutationFn: (rule: DoseRuleView) => approveDoseRule(rule.id, rule.version),
    onSuccess: (data) => {
      queryClient.setQueryData(doseRulesQuery.queryKey, data);
      toasts.show({ title: 'Dose limit approved' });
    },
  });
  const retire = useMutation({
    mutationFn: (reason: string) => {
      if (retiring === null) throw new Error('no rule');
      return retireDoseRule(retiring.id, retiring.version, reason);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(doseRulesQuery.queryKey, data);
      setRetiring(null);
      toasts.show({ title: 'Dose limit retired' });
    },
  });

  if (!mayManage && !mayApprove) {
    return (
      <>
        <PageHeader title="Dose limits" />
        <Notice tone="info">
          You don't have permission to see dose limits. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <SettingsPage title="Dose limits">
      {!online ? (
        <Notice tone="info">Changing dose limits needs a connection to Wodi.</Notice>
      ) : null}
      {rules.data?.noneApproved === true ? (
        <Notice tone="info">
          No approved dose limits yet. Prescribing shows "No dose limit set" for every drug until
          your clinical lead adds them.
        </Notice>
      ) : null}
      {rules.isPending ? <LoadingBlock label="Loading dose limits" rows={6} /> : null}
      {rules.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void rules.refetch()}>
              Try again
            </Button>
          }
        >
          {message(rules.error, 'Dose limits could not load. Try again.')}
        </Notice>
      ) : null}
      {rules.data === undefined ? null : (
        <Table aria-label="Dose limits">
          <TableHeader>
            <Column isRowHeader>For</Column>
            <Column>Kind</Column>
            <Column>Route</Column>
            <Column>Ages</Column>
            <Column>Amount</Column>
            <Column>When exceeded</Column>
            <Column>Source</Column>
            <Column>State</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody items={rules.data.items} renderEmptyState={() => 'No dose limits yet.'}>
            {(rule) => (
              <Row id={rule.id}>
                <Cell>{rule.subjectDisplay}</Cell>
                <Cell>{KIND_LABELS[rule.kind]}</Cell>
                <Cell>{rule.routeCode ?? 'Any'}</Cell>
                <Cell>
                  {ageText(rule.ageFromDays, rule.ageToDays)}
                  {rule.weightFromKg === null
                    ? ''
                    : `, ${rule.weightFromKg} kg${rule.weightToKg === null ? ' and above' : ` to under ${rule.weightToKg} kg`}`}
                </Cell>
                <Cell>{amountText(rule)}</Cell>
                <Cell>{rule.enforcement === 'blocking' ? 'Not allowed' : 'Reason needed'}</Cell>
                <Cell>{rule.source}</Cell>
                <Cell>
                  <Tag state={rule.approval === 'approved' ? 'done' : 'pending'}>
                    {rule.approval === 'approved' ? 'Approved' : 'Provisional'}
                  </Tag>
                </Cell>
                <Cell>
                  {mayApprove &&
                  rule.approval === 'provisional' &&
                  rule.changedBy !== session.user.id ? (
                    <Button
                      dense
                      isDisabled={!online}
                      onPress={() => {
                        approve.mutate(rule);
                      }}
                    >
                      Approve
                    </Button>
                  ) : null}{' '}
                  {mayApprove ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setRetiring(rule);
                      }}
                    >
                      Retire
                    </Button>
                  ) : null}
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      )}
      {approve.isError ? (
        <Notice tone="error">{message(approve.error, 'That was not approved. Try again.')}</Notice>
      ) : null}
      {mayManage && rules.data !== undefined && online ? (
        <AddRule rules={rules.data.items} />
      ) : null}
      <ReasonDialog
        title="Retire this dose limit"
        isOpen={retiring !== null}
        onOpenChange={(open) => {
          if (!open) setRetiring(null);
        }}
        confirmLabel="Retire dose limit"
        pendingLabel="Retiring…"
        isPending={retire.isPending}
        error={
          retire.error === null ? null : message(retire.error, 'That did not work. Try again.')
        }
        onConfirm={(reason) => {
          retire.mutate(reason);
        }}
      />
    </SettingsPage>
  );
}
